import type { SearchBarFilter } from '@/components/search-bar/SearchBar';
import Icon from '@/components/shared/Icons';
import InputWithDatePicker, { type StringsForDateRangePicker } from '@/components/search-bar/InputWithDatePicker';
import type { DateRange } from 'react-day-picker';
import { allowNumbersOnly } from '@/utils/inputHelper';

type Props = {
  filter: SearchBarFilter;
  textBoxClassNames: string;
  isSearchInput?: boolean;
  locale?: Locale;
  selectedRange?: DateRange;
  handleRangeSelect?: (range: DateRange) => void;
  dateRangeAriaLabels?: StringsForDateRangePicker;
};

export default function MobileSearchInput({
  filter,
  textBoxClassNames,
  isSearchInput,
  selectedRange,
  handleRangeSelect,
  locale,
  dateRangeAriaLabels
}: Props): JSX.Element {
  const {
    inputType,
    icon,
    id,
    name,
    inputValue,
    onClick,
    onChange,
    label,
    maxRange,
    showErrorMessage,
    errorMessage,
    blockedDays,
    ref
  } = filter;
  const inputProps = {
    type: inputType,
    id,
    ref,
    className: textBoxClassNames,
    name,
    value: inputValue,
    onChange,
    onClick,
    onFocus: onChange,
    'aria-label': label,
    ...(isSearchInput ? {} : { min: 0, max: maxRange })
  };

  return (
    <div className="flex-1">
      <label htmlFor={id} className="inset-0 mb-2 line-clamp-1 overflow-hidden text-b-sm font-normal text-neutral-60">
        {label}
      </label>
      <div id="searchBoxContainer" className="relative last:mb-4">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
          <Icon id={icon} className="h-4 w-4 fill-neutral-60" />
        </div>
        {id === 'EVENT_START_DATE' || id === 'EVENT_END_DATE' ? (
          <InputWithDatePicker
            inputProps={inputProps}
            blockedDays={blockedDays}
            handleRangeSelect={handleRangeSelect}
            selectedRange={selectedRange}
            locale={locale}
            strings={dateRangeAriaLabels as StringsForDateRangePicker}
          />
        ) : (
          <input
            {...inputProps}
            onKeyDown={event => {
              if (isSearchInput) {
                return;
              }
              if (event.key === 'Escape') {
                event.preventDefault();
              } else {
                allowNumbersOnly(event);
              }
            }}
          />
        )}
      </div>
      {showErrorMessage && (
        <div className="my-2 flex items-center">
          <Icon id="alert-triangle" className="h-4 w-3 fill-danger-60" />
          <div className="ml-2 text-b-sm text-danger-60" aria-label={errorMessage}>
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  );
}
